import { ticketValue, validateFrequencyFilters } from "./frequencyModel";

export const DURATION_INTERVALS = [
  { key: "m15", label: "0–15 min", max: 15 },
  { key: "m30", label: "16–30 min", max: 30 },
  ...Array.from({ length: 9 }, (_, i) => ({
    key: `h${i + 1}`,
    label: `${i + 1} ${i ? "horas" : "hora"}`,
    max: (i + 1) * 60,
  })),
  { key: "over9", label: "9 horas +", max: Infinity },
];
const formatter = new Intl.DateTimeFormat("en-CA", {
  timeZone: "America/Bogota",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});
function timestamp(value) {
  if (typeof value !== "string") return null;
  const match = value.match(
    /^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/i
  );
  if (
    !match ||
    validateFrequencyFilters({ startDate: match[1], endDate: match[1] }) ||
    +match[2] > 23 ||
    +match[3] > 59 ||
    +match[4] > 59
  )
    return null;
  const instant = new Date(value);
  if (Number.isNaN(instant.getTime())) return null;
  const parts = Object.fromEntries(formatter.formatToParts(instant).map((p) => [p.type, p.value]));
  return { ms: instant.getTime(), date: `${parts.year}-${parts.month}-${parts.day}` };
}
export function processPermanenceData(visits, filters) {
  const invalid = validateFrequencyFilters(filters);
  if (invalid) throw new Error(invalid);
  const groups = new Map();
  const cursor = new Date(`${filters.startDate}T12:00:00Z`);
  while (cursor.toISOString().slice(0, 10) <= filters.endDate) {
    const fecha = cursor.toISOString().slice(0, 10);
    groups.set(fecha, {
      fecha,
      total: 0,
      ...Object.fromEntries(DURATION_INTERVALS.map((i) => [i.key, 0])),
    });
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  const selected = (filters.ticketTypes || []).map(ticketValue).filter(Boolean);
  const ids = new Set();
  let incomplete = 0,
    unverifiedVip = 0,
    unclassified = 0;
  for (const visit of visits) {
    if (!visit.entry_at || !visit.exit_at) {
      incomplete += 1;
      continue;
    }
    const entry = timestamp(visit.entry_at),
      exit = timestamp(visit.exit_at);
    if (!entry || !exit || exit.ms < entry.ms)
      throw new Error(
        "El informe contiene una visita con fechas inválidas o una salida anterior a la entrada."
      );
    if (!groups.has(exit.date)) continue;
    const type = ticketValue(visit.ticket_type),
      category = ticketValue(visit.ticket_category || type);
    if (!type) {
      if (selected.length)
        throw new Error("Falta el tipo de tarjeta para aplicar el filtro de permanencia.");
      unclassified += 1;
    }
    if (selected.length && !selected.includes(type) && !selected.includes(category)) continue;
    if (type === "vip" || category === "vip") {
      if (!visit.entry_scope || !visit.exit_scope) {
        unverifiedVip += 1;
        continue;
      }
      if (
        String(visit.entry_scope).toLowerCase() !== "general" ||
        String(visit.exit_scope).toLowerCase() !== "general"
      )
        continue;
    }
    if (visit.id == null || String(visit.id).trim() === "")
      throw new Error("Cada visita debe incluir un identificador único.");
    if (ids.has(String(visit.id))) continue;
    ids.add(String(visit.id));
    const minutes = (exit.ms - entry.ms) / 60000;
    const interval = DURATION_INTERVALS.find((i) => minutes <= i.max);
    const row = groups.get(exit.date);
    row[interval.key] += 1;
    row.total += 1;
  }
  const rows = [...groups.values()];
  const totals = Object.fromEntries(
    DURATION_INTERVALS.map((i) => [i.key, rows.reduce((sum, row) => sum + row[i.key], 0)])
  );
  const warnings = [];
  if (incomplete)
    warnings.push(`${incomplete} visitas sin entrada o salida se excluyeron del informe.`);
  if (unverifiedVip)
    warnings.push(
      `${unverifiedVip} visitas VIP se excluyeron porque falta identificar los accesos generales.`
    );
  if (unclassified)
    warnings.push("Faltan tipos de tarjeta; el conteo de VIP está pendiente de verificar.");
  return {
    rows,
    totals,
    totalVisits: rows.reduce((sum, row) => sum + row.total, 0),
    totalDays: rows.length,
    warnings,
    filters: { ...filters, ticketTypes: [...(filters.ticketTypes || [])] },
  };
}
